import { Filter } from '@searchkit/client';
import { isEqual, remove } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

type UseListViewInitialFilterParams = {
    initialFilter?: Filter;
    immutableFilters?: Filter[];
    onClearInitialFilter?: () => void;
    variables: { filters: Filter[] };
    resetPagination: () => void;
    doRefetch: (skipIfAlreadyQueried?: boolean) => void | Promise<void>;
};

export const useListViewInitialFilter = ({
    initialFilter,
    immutableFilters,
    onClearInitialFilter,
    variables,
    resetPagination,
    doRefetch
}: UseListViewInitialFilterParams) => {
    const prevInitialFilterRef = useRef<Filter | undefined>(undefined);
    const prevAppliedInitialFilterRef = useRef<Filter | undefined>(undefined);
    const resetPaginationRef = useRef(resetPagination);
    const doRefetchRef = useRef(doRefetch);
    const [activeInitialFilter, setActiveInitialFilter] = useState<Filter | undefined>(undefined);
    const [hasInitialFilterResolved, setHasInitialFilterResolved] = useState(false);

    useEffect(() => {
        resetPaginationRef.current = resetPagination;
    }, [resetPagination]);

    useEffect(() => {
        doRefetchRef.current = doRefetch;
    }, [doRefetch]);

    useEffect(() => {
        if (hasInitialFilterResolved || !initialFilter?.identifier) {
            return;
        }

        setActiveInitialFilter(initialFilter);
        setHasInitialFilterResolved(true);
    }, [hasInitialFilterResolved, initialFilter]);

    useEffect(() => {
        const previousInitialFilter = prevInitialFilterRef.current;

        if (previousInitialFilter) {
            const hasImmutableMatch = (immutableFilters || []).some((filter) => isEqual(filter, previousInitialFilter));

            if (!hasImmutableMatch) {
                remove(variables.filters, (filter) => isEqual(filter, previousInitialFilter));
            }
        }

        if (activeInitialFilter) {
            const alreadyExists = variables.filters.some((filter) => isEqual(filter, activeInitialFilter));

            if (!alreadyExists) {
                variables.filters.push(activeInitialFilter);
            }
        }

        prevInitialFilterRef.current = activeInitialFilter;
    }, [activeInitialFilter, immutableFilters, variables]);

    useEffect(() => {
        const previousAppliedInitialFilter = prevAppliedInitialFilterRef.current;
        const hasActiveInitialFilter = Boolean(activeInitialFilter?.identifier);
        const hadActiveInitialFilter = Boolean(previousAppliedInitialFilter?.identifier);

        if (!hasActiveInitialFilter && !hadActiveInitialFilter) {
            return;
        }

        if (hasActiveInitialFilter && isEqual(previousAppliedInitialFilter, activeInitialFilter)) {
            return;
        }

        prevAppliedInitialFilterRef.current = activeInitialFilter;

        resetPaginationRef.current();
        // Refetch on initial-filter apply/clear, but dedupe when useQuery already ran with identical variables.
        doRefetchRef.current(true);
    }, [activeInitialFilter]);

    const handleClearInitialFilter = useCallback(() => {
        if (!activeInitialFilter?.identifier) {
            onClearInitialFilter?.();
            return;
        }

        const hasImmutableMatch = (immutableFilters || []).some((filter) => isEqual(filter, activeInitialFilter));

        if (!hasImmutableMatch) {
            remove(
                variables.filters,
                (filter) =>
                    filter.identifier === activeInitialFilter.identifier &&
                    (activeInitialFilter.value === undefined || filter.value === activeInitialFilter.value)
            );
        }

        setActiveInitialFilter(undefined);
        setHasInitialFilterResolved(true);

        onClearInitialFilter?.();
    }, [activeInitialFilter, immutableFilters, onClearInitialFilter, variables.filters]);

    return {
        activeInitialFilter,
        handleClearInitialFilter
    };
};
